import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import { selectLmsLists } from '../../store/features/lmsSlice';
import { LoadingSpinner } from '../Loading/LoadingSpinner';
import { Menu } from 'antd';
import { getSidebarItems } from './sidebarItem';
import { changeTimestamp, isEndDateToday } from '../../api/utils/util-func';
import { LmsLinkItemKey } from '../../constants/types/lms/type';
import { openModal } from '../../store/features/modalSlice';
import { useLmsGuideLink } from '../../hooks/lms/useLmsGuideLink';
import ModalExtendLink from '../modal/ModalExtendLink';
import { getCourseConfig } from '../../api/utils/lms/utils';

const Sidebar = ({ classId }: { classId: string }) => {
  const { pathname } = useLocation();
  const lmsLists = useAppSelector(selectLmsLists);
  const [isLoading, setIsloading] = useState(false);

  /**
   * 현재 코스의 분류 결정
   * @description setMenuExposure 함수의 인자
   * - "home" : 젭스영어교실 베이직
   * - "church" : 교회학교 HFHJ, WMW
   * - "" : 그 외
   */
  useEffect(() => {
    setIsloading(true);
    if (lmsLists) {
      setIsloading(false);
    }
  }, [lmsLists]);

  const courseConfig =
    classId === lmsLists?.id ? getCourseConfig(lmsLists.course?.id) : undefined;

  const courseName = courseConfig?.name ?? 'OTHERS';

  const dispatch = useAppDispatch();

  const { openGuide: handleClickLmsGuide } = useLmsGuideLink(
    LmsLinkItemKey.LMS_GUIDE,
  );

  const extendButtonHandler = () => {
    dispatch(
      openModal({
        body: <ModalExtendLink />,
      }),
    );
  };

  /**
   * 사이드 메뉴의 하이라이트 표시
   * @returns 현재 메뉴
   */
  const nowMenu = useMemo(() => {
    const urlArr = pathname.split('/');

    if (urlArr.length < 3 || urlArr[1] !== 'lms') {
      console.error('잘못된 접속입니다.', pathname);
      return;
    }

    const base = urlArr[3];
    let subType = urlArr[4];
    switch (base) {
      case 'dashboard':
      case 'take-class':
      case 'voucher':
      case 'dalant':
      case 'membership-store':
      case 'student':
      case 'class-group':
      case 'resources':
        return base;
      case 'assignment':
        subType = urlArr[4];
        return ['create', 'status'].includes(subType)
          ? `${base}-${subType}`
          : undefined;
      case 'recitation':
        subType = urlArr[4];
        return ['create', 'status'].includes(subType)
          ? `${base}-${subType}`
          : undefined;
      default:
        return;
    }
  }, [pathname]);

  const remainDaysLabel = useMemo(() => {
    if (Number(lmsLists?.remain_days) > 0) {
      return `${lmsLists?.remain_days}일 (${changeTimestamp(
        lmsLists?.end_date,
      )} 까지)`;
    }
    if (
      Number(lmsLists?.remain_days) === 0 &&
      isEndDateToday(lmsLists?.end_date)
    ) {
      return `${lmsLists?.remain_days}일 (${changeTimestamp(lmsLists?.end_date)} 오늘까지)`;
    }
    return `${lmsLists?.remain_days}일`;
  }, [lmsLists?.remain_days, lmsLists?.end_date]);

  return isLoading ? (
    <LoadingSpinner message="데이터 조회중" />
  ) : (
    <div>
      {lmsLists && (
        <div className="border-b bg-gray0 px-5 py-3 xl:bg-white xl:px-7 xl:py-7">
          <Link to="./dashboard">
            <div className="text-lg font-semibold">{lmsLists.course?.name}</div>
          </Link>

          <div className="mt-3 flex flex-col gap-1 text-sm">
            <div className="font-semibold text-gray6">이용 잔여일</div>

            <div className="font-medium text-red5">{remainDaysLabel}</div>
          </div>

          <button
            type="button"
            onClick={extendButtonHandler}
            className="mt-4 flex h-8 w-full cursor-pointer items-center justify-center rounded-lg border border-purple5 bg-white text-sm font-bold text-purple5 transition-colors hover:bg-purple1 active:scale-[0.99]"
          >
            이용 기간 연장
          </button>
        </div>
      )}
      <Menu
        mode="inline"
        defaultOpenKeys={['assignment', 'recitation']}
        selectedKeys={[nowMenu]}
        items={getSidebarItems(courseName, handleClickLmsGuide)}
      />
    </div>
  );
};

export default Sidebar;
